import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Flex,
  Heading,
  LoadingOrbit,
  Text,
  TextField,
  useToast,
} from '@chatsdet/ui';
import { Key, Plus, ShieldCheck } from '@phosphor-icons/react';
import { normalizeTestSignInDomain } from '@chatsdet/types';
import { extractUuid } from '../../../../lib/prefixed-ids.js';
import { API_URL } from '../../../../config.js';
import { useCreateProviderMutation, useTestSignInQuery } from './useTestSignIn';
import { TestSignInProviderCard } from './TestSignInProviderCard';
import { TestSignInClientRow } from './TestSignInClientRow';
import { TestSignInAddClientDialog } from './TestSignInAddClientDialog';
import { TestSignInSecretDialog } from './TestSignInSecretDialog';
import { TestSignInSetupPromptDialog } from './TestSignInSetupPromptDialog';
import { TestSignInActivityPanel } from './TestSignInActivityPanel';
import type { TestSignInClient } from './types';

type PropertySummary = { id: string; name: string };

function usePropertiesQuery() {
  return useQuery({
    queryKey: ['properties'],
    queryFn: async (): Promise<{ items: PropertySummary[] }> => {
      const r = await fetch(`${API_URL}/org/properties`, { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to fetch properties');
      return r.json();
    },
  });
}

type TestSignInSectionProps = {
  isAdmin: boolean;
};

export function TestSignInSection({ isAdmin }: TestSignInSectionProps) {
  const { toast } = useToast();
  const testSignInQuery = useTestSignInQuery();
  const propertiesQuery = usePropertiesQuery();

  const [domainInput, setDomainInput] = React.useState('');
  const createProvider = useCreateProviderMutation();

  const [isAddOpen, setIsAddOpen] = React.useState(false);
  const [editingClient, setEditingClient] = React.useState<TestSignInClient | null>(null);
  const [revealSecret, setRevealSecret] = React.useState<string | null>(null);
  const [instructionsClient, setInstructionsClient] = React.useState<TestSignInClient | null>(null);

  if (testSignInQuery.isLoading) {
    return (
      <Flex align="center" justify="center" className="py-12">
        <LoadingOrbit size="md" />
      </Flex>
    );
  }

  const provider = testSignInQuery.data?.data.provider ?? null;
  const clients = testSignInQuery.data?.data.clients ?? [];
  const properties = propertiesQuery.data?.items ?? [];
  const propertyById = new Map(properties.map((p) => [p.id, p]));

  const propertyLabel = (client: TestSignInClient) => {
    const uuid = extractUuid(client.propertyId);
    const property = uuid ? propertyById.get(uuid) : undefined;
    return property?.name ?? client.propertyId;
  };

  const usedPropertyIds = new Set<string>(
    clients.map((c) => extractUuid(c.propertyId)).filter((id): id is NonNullable<typeof id> => !!id)
  );
  const availableProperties = properties
    .filter((p) => !usedPropertyIds.has(p.id))
    .map((p) => ({ id: p.id, label: p.name }));

  const handleSetUp = async () => {
    const normalized = normalizeTestSignInDomain(domainInput);
    if (!normalized) return;
    try {
      await createProvider.mutateAsync(normalized);
      toast.success('Test sign-in set up');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to set up test sign-in');
    }
  };

  return (
    <Box className="space-y-6">
      <Box>
        <Heading level={2}>Test sign-in</Heading>
        <Text size="sm" className="text-muted-foreground mt-1">
          Cofactor signs your test users into staging through your app&apos;s real login, acting as
          an OpenID Connect provider for the email domain your test users live in.
        </Text>
      </Box>

      {!provider ? (
        <Card padding="lg">
          <CardContent>
            <Flex direction="column" gap="md">
              <Flex align="start" gap="md">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <ShieldCheck size={24} className="text-primary" weight="bold" />
                </div>
                <Box>
                  <Text weight="medium">Not set up yet</Text>
                  <Text size="sm" className="text-muted-foreground mt-1">
                    Choose the email domain your test users live in. Cofactor will only ever vouch
                    for identities inside that domain — never for anyone else.
                  </Text>
                </Box>
              </Flex>
              {isAdmin ? (
                <Flex gap="sm" align="end">
                  <Box className="flex-1 max-w-sm">
                    <TextField
                      label="Test-user email domain"
                      placeholder="qa.example.com"
                      value={domainInput}
                      onChange={setDomainInput}
                      isDisabled={createProvider.isPending}
                    />
                  </Box>
                  <Button
                    variant="primary"
                    onPress={handleSetUp}
                    isDisabled={!normalizeTestSignInDomain(domainInput) || createProvider.isPending}
                  >
                    {createProvider.isPending ? 'Setting up...' : 'Set up'}
                  </Button>
                </Flex>
              ) : (
                <Text size="sm" className="text-muted-foreground">
                  Only organization admins can set up test sign-in.
                </Text>
              )}
              {createProvider.isError && (
                <Text size="sm" className="text-destructive">
                  {createProvider.error instanceof Error
                    ? createProvider.error.message
                    : 'Failed to set up test sign-in'}
                </Text>
              )}
            </Flex>
          </CardContent>
        </Card>
      ) : (
        <>
          <TestSignInProviderCard provider={provider} isAdmin={isAdmin} />

          <Card padding="lg">
            <CardHeader>
              <Flex align="center" justify="between" gap="md">
                <Box>
                  <Heading level={3}>Applications</Heading>
                  <Text size="sm" className="text-muted-foreground mt-1">
                    One OAuth client per application, with exact callback URLs.
                  </Text>
                </Box>
                {isAdmin && (
                  <Button
                    variant="primary"
                    size="sm"
                    iconLeft={<Plus size={16} />}
                    onPress={() => {
                      setEditingClient(null);
                      setIsAddOpen(true);
                    }}
                    isDisabled={availableProperties.length === 0}
                  >
                    Add application
                  </Button>
                )}
              </Flex>
            </CardHeader>
            <CardContent>
              {clients.length === 0 ? (
                <Box className="text-center py-10">
                  <Key size={40} className="mx-auto text-muted-foreground mb-3" weight="light" />
                  <Text className="text-muted-foreground">No applications yet</Text>
                  <Text size="sm" className="text-muted-foreground mt-1">
                    Add an application to register its callback URLs and get a client secret.
                  </Text>
                </Box>
              ) : (
                <Box className="space-y-3">
                  {clients.map((client) => (
                    <TestSignInClientRow
                      key={client.id}
                      client={client}
                      propertyLabel={propertyLabel(client)}
                      isAdmin={isAdmin}
                      onEdit={() => {
                        setEditingClient(client);
                        setIsAddOpen(true);
                      }}
                      onSetupInstructions={() => setInstructionsClient(client)}
                      onSecretRotated={(secret) => setRevealSecret(secret)}
                    />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          <Card padding="lg">
            <CardHeader>
              <Heading level={3}>Activity</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                Recent sign-ins and events across every application.
              </Text>
            </CardHeader>
            <CardContent>
              <TestSignInActivityPanel />
            </CardContent>
          </Card>

          <TestSignInAddClientDialog
            isOpen={isAddOpen}
            onOpenChange={(open) => {
              setIsAddOpen(open);
              if (!open) setEditingClient(null);
            }}
            availableProperties={availableProperties}
            editingClient={
              editingClient ? { client: editingClient, propertyLabel: propertyLabel(editingClient) } : null
            }
            onCreated={(result) => setRevealSecret(result.clientSecret)}
          />

          <TestSignInSecretDialog secret={revealSecret} onClose={() => setRevealSecret(null)} />

          {instructionsClient && (
            <TestSignInSetupPromptDialog
              isOpen={!!instructionsClient}
              onOpenChange={(open) => {
                if (!open) setInstructionsClient(null);
              }}
              appName={propertyLabel(instructionsClient)}
              issuer={provider.issuer}
              clientId={instructionsClient.id}
              redirectUris={instructionsClient.redirectUris}
              emailDomain={provider.emailDomain}
            />
          )}
        </>
      )}
    </Box>
  );
}
